/**
 * FERP Ceremony Defaults Hook
 *
 * Resolves default signing participants based on document category.
 * Phase 1: Local category-to-role mapping + DEV_ACTOR_MAP resolution.
 * Phase 2: Replace with backend getCeremonyRolesForDocument query.
 *
 * FERP Principles:
 * - Data-driven: role mapping derived from wf-4-0-109 (care plan ceremony)
 * - Dumb Frontend: this is a display hint — backend validates on createSigningCeremony
 */

import { useMemo } from 'react';
import { SignerRole } from '@/generated/graphql';
import { getActorsForCeremonyRole, getAllDevActors, type DevActor } from '@/auth/dev-actors';

export interface ParticipantDefault {
  name: string;
  email: string;
  role: string;
  fieldName: string;
  ceremonyRole: string;
  /** Whether this participant has been assigned to an actor */
  assigned: boolean;
}

export interface ActorOption {
  value: string;
  label: string;
  name: string;
  email: string;
  actorRole: string;
}

interface CeremonyDefaults {
  defaultParticipants: ParticipantDefault[];
  availableActors: ActorOption[];
}

/**
 * Document category → required ceremony signing roles.
 * Derived from wf-4-0-109 (care plan) and MN 144G compliance rules.
 */
const CATEGORY_TO_ROLES: Record<string, Array<{ ceremonyRole: string; signerRole: string; label: string }>> = {
  care: [
    { ceremonyRole: 'resident_or_representative', signerRole: SignerRole.SIGNER, label: 'Resident / Representative' },
    { ceremonyRole: 'designated_coordinator', signerRole: SignerRole.SIGNER, label: 'Designated Coordinator' },
    { ceremonyRole: 'licensed_nurse', signerRole: SignerRole.SIGNER, label: 'Licensed Nurse (RN/LPN)' },
  ],
  safety: [
    { ceremonyRole: 'author', signerRole: SignerRole.SIGNER, label: 'Author' },
    { ceremonyRole: 'witness', signerRole: SignerRole.WITNESS, label: 'Witness' },
  ],
  compliance: [
    { ceremonyRole: 'author', signerRole: SignerRole.SIGNER, label: 'Author' },
    { ceremonyRole: 'approver', signerRole: SignerRole.APPROVER, label: 'Approver (ALD)' },
  ],
  hr: [
    { ceremonyRole: 'author', signerRole: SignerRole.SIGNER, label: 'Author' },
    { ceremonyRole: 'approver', signerRole: SignerRole.APPROVER, label: 'Approver (ALD)' },
  ],
  billing: [
    { ceremonyRole: 'author', signerRole: SignerRole.SIGNER, label: 'Billing Author' },
    { ceremonyRole: 'approver', signerRole: SignerRole.APPROVER, label: 'Approver (ALD)' },
  ],
};

/** Fallback when category is unknown */
const DEFAULT_ROLES = [
  { ceremonyRole: 'author', signerRole: SignerRole.SIGNER, label: 'Signer' },
];

/**
 * Resolve default ceremony participants from document category.
 *
 * For each required role, attempts to find a matching dev actor.
 * Unresolved roles return placeholder entries with assigned=false.
 */
export function useCeremonyDefaults(
  documentCategory: string | undefined,
  currentUserName?: string,
  currentUserEmail?: string,
): CeremonyDefaults {
  const defaults = useMemo(() => {
    const roles = CATEGORY_TO_ROLES[documentCategory ?? ''] ?? DEFAULT_ROLES;

    const participants: ParticipantDefault[] = roles.map((roleDef, idx) => {
      const actors = getActorsForCeremonyRole(roleDef.ceremonyRole);

      // For "author" role, prefer current user
      if (roleDef.ceremonyRole === 'author' && currentUserName && currentUserEmail) {
        return {
          name: currentUserName,
          email: currentUserEmail,
          role: roleDef.signerRole,
          fieldName: `sig_${idx + 1}`,
          ceremonyRole: roleDef.ceremonyRole,
          assigned: true,
        };
      }

      // Auto-assign first matching actor (if available)
      const firstActor = actors[0];
      if (firstActor) {
        return {
          name: firstActor.name,
          email: firstActor.email,
          role: roleDef.signerRole,
          fieldName: `sig_${idx + 1}`,
          ceremonyRole: roleDef.ceremonyRole,
          assigned: true,
        };
      }

      // Unresolved — grey placeholder
      return {
        name: '',
        email: '',
        role: roleDef.signerRole,
        fieldName: `sig_${idx + 1}`,
        ceremonyRole: roleDef.ceremonyRole,
        assigned: false,
      };
    });

    // Build actor options for Select dropdowns
    const allActors = getAllDevActors();
    const actorOptions: ActorOption[] = allActors.map((a: DevActor) => ({
      value: a.id,
      label: `${a.name} (${a.role})`,
      name: a.name,
      email: a.email,
      actorRole: a.role,
    }));

    return { defaultParticipants: participants, availableActors: actorOptions };
  }, [documentCategory, currentUserName, currentUserEmail]);

  return defaults;
}

/**
 * Get the display label for a ceremony role.
 */
export function getCeremonyRoleLabel(ceremonyRole: string): string {
  for (const roles of Object.values(CATEGORY_TO_ROLES)) {
    const match = roles.find((r) => r.ceremonyRole === ceremonyRole);
    if (match) return match.label;
  }
  return ceremonyRole;
}
